import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiEnvelope } from '../models/auth.model';
import { MenuItem, ModifierGroup } from '../models/menu.model';

/** Nullable calorie block, exactly as the POS stores it (null ≠ 0). */
export interface VariantCalories { unit: number | null; per100g: number | null; servingLabel: string | null; }
export interface ItemVariant { id: string; name: string; price: number; calories: VariantCalories; soldOut: boolean; }
export interface ItemModifier { id: string; name: string; isIngredient: boolean; soldOut: boolean; }
export interface ItemModifierGroup { id: string; name: string; min: number; max: number; required: boolean; modifiers: ItemModifier[]; }
export interface ItemAddon { id: string; name: string; price: number; soldOut: boolean; }
export interface ItemAddonGroup { id: string; name: string; addons: ItemAddon[]; }

/** Full item payload for the details sheet — GET /api/v1/menu/items/{id}. */
export interface ItemDetail {
  id: string;
  name: string;
  description: string | null;
  type: string;
  /** lowest variant price on this branch (customer-paid, EGP) */
  priceFrom: number;
  imageUrl: string | null;
  images: string[];
  variants: ItemVariant[];
  modifierGroups: ItemModifierGroup[];
  addonGroups: ItemAddonGroup[];
  allergens: string[];
  flags: string[];
  popularVariantId: string | null;
  /** The whole item is off sale on this branch (item-level flag from the API). */
  soldOut: boolean;
}

/** Synthetic group id used when variants are surfaced as a single-select. */
export const VARIANT_GROUP_ID = '__variant';

@Injectable({ providedIn: 'root' })
export class ItemService {
  private readonly base = environment.apiBase;

  constructor(private http: HttpClient) {}

  /** One item's full details, priced for the branch (headers add brand + locale). */
  getItem(id: string, branchId: string): Observable<ItemDetail> {
    const params = new HttpParams().set('branch_id', branchId);
    return this.http
      .get<ApiEnvelope<any>>(`${this.base}/api/v1/menu/items/${encodeURIComponent(id)}`, { params })
      .pipe(map((r) => mapItem(r.data || {})));
  }
}

function mapCalories(c: any): VariantCalories {
  c = c || {};
  return { unit: c.unit ?? null, per100g: c.per_100g ?? null, servingLabel: c.serving_size_label ?? null };
}

function mapItem(d: any): ItemDetail {
  return {
    id: d.id,
    name: d.name,
    description: d.description ?? null,
    type: d.type || 'unit',
    // Item/variant prices are minor units (piasters) → major units.
    priceFrom: (d.price_from ?? 0) ,
    imageUrl: d.image_url ?? null,
    images: d.images || [],
    variants: (d.variants || []).map((v: any): ItemVariant => ({
      id: v.id, name: v.name, price: (v.price ?? 0) , calories: mapCalories(v.calories), soldOut: !!v.sold_out,
    })),
    modifierGroups: (d.modifier_groups || []).map((g: any): ItemModifierGroup => ({
      id: g.id, name: g.name, min: g.min ?? 0, max: g.max ?? 1, required: !!g.required,
      modifiers: (g.modifiers || []).map((m: any): ItemModifier => ({
        id: m.id, name: m.name, isIngredient: !!m.is_ingredient, soldOut: !!m.sold_out,
      })),
    })),
    addonGroups: (d.addon_groups || []).map((g: any): ItemAddonGroup => ({
      id: g.id, name: g.name,
      // Addon prices arrive already in major units (EGP) — do NOT divide.
      addons: (g.addons || []).map((a: any): ItemAddon => ({ id: a.id, name: a.name, price: a.price ?? 0, soldOut: !!a.sold_out })),
    })),
    allergens: d.allergens || [],
    flags: d.flags || [],
    popularVariantId: d.popular_variant_id ?? null,
    soldOut: !!(d.sold_out || d.is_sold_out || d.is_available === false || d.available === false),
  };
}

/**
 * Adapt a live ItemDetail into the ItemSheet's MenuItem view model so the sheet's
 * existing selection/pricing/validation logic renders it unchanged:
 *  - variants  → a required single-select (only when there's a real choice); each
 *                option's price is its delta over the cheapest variant, so the
 *                header total shows the selected variant's price. The popular
 *                variant (if any) is flagged so the sheet preselects it; otherwise
 *                the cheapest (first) is preselected.
 *  - modifiers → free option groups (single when max=1, else multi), min/max/required.
 *  - addons    → optional priced multi-select groups.
 */
export function detailToMenuItem(d: ItemDetail, fallbackImage: string): MenuItem {
  const variants = (d.variants.length
    ? [...d.variants]
    : [{ id: d.id, name: d.name, price: d.priceFrom, calories: { unit: null, per100g: null, servingLabel: null }, soldOut: false }]
  ).sort((a, b) => a.price - b.price);

  const base = variants[0].price;
  // Sold-out variants can't be preselected — fall to the cheapest one still on sale.
  const popular = d.popularVariantId ? variants.find((v) => v.id === d.popularVariantId && !v.soldOut) : undefined;
  const preselected = popular || variants.find((v) => !v.soldOut) || variants[0];

  const groups: ModifierGroup[] = [];

  if (variants.length > 1) {
    groups.push({
      id: VARIANT_GROUP_ID, name: 'Your Choice Of Size', type: 'single', required: true, min: 1, max: 1,
      apiKind: 'variant',
      options: variants.map((v) => ({
        id: v.id, name: v.name, price: +(v.price - base).toFixed(2), popular: v.id === (popular?.id ?? ''), soldOut: v.soldOut,
      })),
    });
  }

  d.modifierGroups.forEach((g) => {
    groups.push({
      id: g.id, name: g.name, type: g.max > 1 ? 'multi' : 'single',
      required: g.required, min: g.min, max: Math.max(1, g.max), apiKind: 'modifier',
      options: g.modifiers.map((m) => ({ id: m.id, name: m.name, price: 0, soldOut: m.soldOut })),
    });
  });

  d.addonGroups.forEach((g) => {
    groups.push({
      id: g.id, name: g.name, type: 'multi', required: false, min: 0, max: Math.max(1, g.addons.length),
      apiKind: 'addon',
      options: g.addons.map((a) => ({ id: a.id, name: a.name, price: a.price, soldOut: a.soldOut })),
    });
  });

  const variantKcal: Record<string, number | null> = {};
  variants.forEach((v) => (variantKcal[v.id] = v.calories.unit));

  return {
    id: d.id, cat: '', name: d.name, price: base, tone: 'beef', type: d.type,
    desc: d.description || '', kcal: preselected.calories.unit ?? 0,
    tags: [], mods: groups, allergens: d.allergens,
    image: d.images[0] || d.imageUrl || fallbackImage || null,
    variantKcal,
    apiVariantId: variants[0].id,
    // Sold out as a whole: flagged by the API, or every size is sold out (this also covers a
    // single-size item, which has no size picker to grey out).
    soldout: d.soldOut || (d.variants.length > 0 && d.variants.every((v) => v.soldOut)),
  };
}
